import React, { useState, useEffect, useMemo } from "react";
import {
  Avatar,
  Grid,
  List,
  Paper,
  Stack,
  Typography,
  Box,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { format, startOfDay, isValid, parseISO } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { appSelector } from "../../redux/selector";
import { userSelector } from "../../user-redux/selector";
import Key from "../../../clientVariables.json";
import {
  getcandidateSubmissionsAction,
  selectedSubmissionAction,
  selectedJobAction,
} from "../../redux/action";
import { Button } from "react-bootstrap";
import HistoryIcon from "@mui/icons-material/History";
import KeyboardBackspaceRoundedIcon from "@mui/icons-material/KeyboardBackspaceRounded";

const Admininterviews = () => {
  const dispatch = useDispatch();
  const {
    candidateSubmissions,
    selectedCandidate,
    selectedRecruiter,
    assignedCandidates,
  } = useSelector(appSelector);
  const { user } = useSelector(userSelector);
  const [interviewTimes, setInterviewTimes] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState({});
  const [selectedJobInfo, setSelectedJobInfo] = useState({}); // Store job info when showing history

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "short" });
    return `${month} ${day}`;
  };

  useEffect(() => {
    const fetchInterviewTimes = async () => {
      try {
        if (!selectedCandidate?.candidate_id) return;

        const response = await fetch(
          `${Key.domain}/candidate/${selectedCandidate?.candidate_id}/scheduled_interviews`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );
        const data = await response.json();
        setInterviewTimes(data?.data?.interviews || []);
      } catch (error) {
        console.error("Error fetching interview times:", error);
      }
    };

    if (selectedRecruiter?.recruiter_id && selectedCandidate?.candidate_id) {
      fetchInterviewTimes();
    }
  }, [selectedRecruiter?.recruiter_id, selectedCandidate?.candidate_id]);

  useEffect(() => {
    setShowHistory(false)
    setHistoryItems([]);
    setSelectedJobInfo({});
  }, [candidateSubmissions, selectedCandidate?.candidate_id])

  const latestInterviewsByJobId = useMemo(() => {
    const map = {};
    for (const itw of interviewTimes || []) {
      if (!itw?.jobid) continue;
      const curr = map[itw.jobid];
      if (!curr) {
        map[itw.jobid] = itw;
      } else {
        const a = new Date(itw.updated_at);
        const b = new Date(curr.updated_at);
        if (a > b) map[itw.jobid] = itw;
      }
    }
    return map;
  }, [interviewTimes]);

  const startToday = startOfDay(new Date());

  const filteredSubmissions = useMemo(() => {
    return (candidateSubmissions || [])
      .filter((job) => {
        const interview = latestInterviewsByJobId[job?.job_id];
        if (!interview) return false;

        const d = parseISO(interview.interview_date);
        if (!isValid(d)) return false;

        return d;
      })
      .slice(0, 100);
  }, [candidateSubmissions, latestInterviewsByJobId, startToday]);

  const getInterviewHistory = async (candidateid, jobid) => {
    const loadingKey = `${candidateid}-${jobid}`;
    setLoadingHistory((prev) => ({ ...prev, [loadingKey]: true }));

    try {
      const response = await fetch(
        `${Key.domain}/candidate/scheduled_interviews_history`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            candidateid,
            jobid,
          }),
        }
      );
      const data = await response.json();
      setHistoryItems(data?.data || []);
      setShowHistory(true);

      // Store the job info for the selected history
      const job = candidateSubmissions.find(j => j.job_id === jobid);
      const interviewInfo = latestInterviewsByJobId[jobid];
      if (job || interviewInfo) {
        setSelectedJobInfo({
          client_name: interviewInfo?.client_name || job?.client_name || "Unknown Client",
          job_title: job?.job_title || "Unknown Title",
          candidate_name: selectedCandidate?.candidate_name || "Candidate"
        });
      }
    } catch (error) {
      console.error("error", error);
    } finally {
      setLoadingHistory((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleBack = () => {
    setShowHistory(false);
    setHistoryItems([]);
    setSelectedJobInfo({});
  };

  const isHistoryLoading = (candidateid, jobid) => {
    const key = `${candidateid}-${jobid}`;
    return loadingHistory[key] || false;
  };

  // Function to get border color based on status
  const getBorderColor = (status) => {
    if (!status) return "#1976d2";

    const statusStr = String(status).toLowerCase();

    if (statusStr === "selected" || statusStr === "1") {
      return "#81c784";
    } else if (statusStr === "rejected") {
      return "#e57373";
    } else if (statusStr.includes("round") || statusStr.includes("interview")) {
      return "#1976d2";
    } else {
      return "#9e9e9e";
    }
  };

  return (
    <div>
      <Box>

        <Grid container spacing={1} style={{ padding: "10px" }}>
          {!showHistory ? (
            filteredSubmissions.length === 0 ||
              assignedCandidates?.filter((c) => c.status === 1).length === 0 ? (
              <Grid item xs={12}>
                <Typography
                  variant="body1"
                  align="center"
                  style={{ margin: "10px", fontSize: "12px" }}
                >
                  No interviews found for the selected candidate.
                </Typography>
              </Grid>
            ) : (
              filteredSubmissions.map((job, index) => {
                const interviewTime = latestInterviewsByJobId[job?.job_id];
                const isLoading = isHistoryLoading(
                  job?.candidate_id,
                  job?.job_id
                );

                return (
                  <Grid item xs={12} key={index}>
                    <Paper elevation={2} style={{ padding: "10px" }}>
                      <Grid container spacing={1} alignItems="center">
                        <Grid
                          item
                          xs={3}
                          sm={2}
                          style={{
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <Box
                            sx={{
                              width: { xs: 44, sm: 50 },
                              height: { xs: 40, sm: 46 },
                              display: "flex",
                              flexDirection: "column",
                              borderRadius: "8px",
                              overflow: "hidden",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.12)",
                              transition: "transform 0.2s ease",
                              "&:hover": {
                                transform: "translateY(-2px)",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                backgroundColor: "#1a73e8",
                                color: "#fff",
                                textAlign: "center",
                                py: { xs: 0.2, sm: 0.3 },
                                px: 0.5,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: { xs: "8px", sm: "9px" },
                                  fontWeight: "500",
                                  textTransform: "uppercase",
                                }}
                              >
                                {interviewTime?.interview_date
                                  ? format(
                                    parseISO(interviewTime.interview_date),
                                    "MMM"
                                  )
                                  : ""}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                flex: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#fff",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: { xs: "18px", sm: "20px" },
                                  fontWeight: "400",
                                  color: "#3c4043",
                                }}
                              >
                                {interviewTime?.interview_date
                                  ? format(
                                    parseISO(interviewTime.interview_date),
                                    "dd"
                                  )
                                  : ""}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>

                        <Grid item xs={9} sm={10}>
                          <Stack spacing={0.5}>
                            <Typography
                              variant="body1"
                              style={{
                                fontSize: "12px",
                                fontWeight: "600",
                              }}
                            >
                              {interviewTime?.client_name}
                            </Typography>

                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                style={{ fontSize: "11px" }}
                              >
                                {interviewTime?.interview_time}
                                &nbsp;|&nbsp;
                                {interviewTime?.status === "1"
                                  ? "First Round"
                                  : interviewTime?.status}
                              </Typography>

                              <Tooltip
                                title={isLoading ? "Loading..." : "History"}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 24,
                                    height: 24,
                                    cursor: isLoading ? "default" : "pointer",
                                  }}
                                >
                                  {isLoading ? (
                                    <CircularProgress
                                      size={20}
                                      thickness={4}
                                      style={{ color: "#1976d2" }}
                                    />
                                  ) : (
                                    <HistoryIcon
                                      fontSize="small"
                                      style={{
                                        color: "#9e9e9e",
                                        opacity: 0.8,
                                      }}
                                      onClick={() =>
                                        getInterviewHistory(
                                          job?.candidate_id,
                                          job?.job_id
                                        )
                                      }
                                    />
                                  )}
                                </Box>
                              </Tooltip>
                            </Box>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                );
              })
            )
          ) : historyItems.length === 0 ? (
            <Grid item xs={12}>
              <KeyboardBackspaceRoundedIcon
                onClick={handleBack}
                sx={{
                  cursor: "pointer",
                  fontSize: { xs: 18, sm: 20 },
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body1"
                align="center"
                style={{ margin: "10px", fontSize: "12px" }}
              >
                No history found for this interview
              </Typography>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Paper
                elevation={2}
                style={{
                  padding: "15px",
                  borderRadius: "8px",
                }}
              >
                <Box sx={{ mb: 2, pb: 2, borderBottom: "1px solid #e0e0e0" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <KeyboardBackspaceRoundedIcon
                      onClick={handleBack}
                      sx={{
                        cursor: "pointer",
                        fontSize: { xs: 18, sm: 20 },
                        flexShrink: 0,
                      }}
                    />
                    <Tooltip
                      title={selectedJobInfo.client_name || historyItems[0]?.client_name || "Interview History"}
                      arrow
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontSize: { xs: "13px", sm: "14px" },
                          fontWeight: 600,
                          color: "#3c4043",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {selectedJobInfo.client_name || historyItems[0]?.client_name || "Interview History"}
                      </Typography>
                    </Tooltip>


                  </Box>
                </Box>
                <Box sx={{ position: "relative", pl: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontSize: { xs: "11px", sm: "12px" },
                      fontWeight: 600,
                      color: "#5f6368",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      minWidth: 0,
                      paddingBottom: 2
                    }}
                  >
                    <span style={{ opacity: 0.8 }}>Resume:</span>
                    <span
                      style={{
                        fontWeight: 700,
                        color: "#1a73e8",
                        textDecoration: "underline",
                        textUnderlineOffset: "2px",
                      }}
                    >
                      {historyItems[0]?.filename || "Not available"}
                    </span>
                  </Typography>

                  {historyItems.map((item, index) => {
                    const borderColor = getBorderColor(item?.status);

                    return (
                      <Box
                        key={index}
                        sx={{
                          position: "relative",
                          pl: 3,
                          pb: index !== historyItems.length - 1 ? 2 : 1,
                        }}
                      >
                        {index !== historyItems.length - 1 && (
                          <Box
                            sx={{
                              position: "absolute",
                              left: 6,
                              top: 14,
                              bottom: -8,
                              borderLeft: "1px dashed rgba(0,0,0,0.3)",
                            }}
                          />
                        )}
                        <Box
                          sx={{
                            position: "absolute",
                            left: 0,
                            top: 6,
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            border: `2px solid ${borderColor}`,
                            backgroundColor: "#fff",
                            zIndex: 1,
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Box
                          sx={{
                            backgroundColor: "#f9f9f9",
                            borderRadius: "6px",
                            padding: "8px 10px",
                            border: "1px solid #f0f0f0",
                          }}
                        >
                          <Stack direction="column" sx={{ width: "100%" }}>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontWeight: 600,
                                m: 0,
                                lineHeight: 1.2,
                                color: "#3c4043",
                              }}
                            >
                              {item?.status === "1"
                                ? "First Round"
                                : item?.status || "N/A"}
                            </Typography>

                            {item?.interview_time && item?.interview_date ? (
                              <Typography
                                sx={{
                                  fontSize: "11px",
                                  textTransform: "capitalize",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  m: 0,
                                  lineHeight: 1.2,
                                  color: "#5f6368",
                                  marginTop: "2px",
                                }}
                              >
                                {item?.interview_time} &nbsp;|&nbsp;
                                {item?.interview_date}
                              </Typography>
                            ) : (
                              <Typography
                                sx={{
                                  fontSize: "10px",
                                  color: "#9e9e9e",
                                  fontStyle: "italic",
                                  marginTop: "2px",
                                }}
                              >
                                No date/time specified
                              </Typography>
                            )}
                          </Stack>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Box>
    </div>
  );
};

export default Admininterviews;